import { Box, Typography, Alert, Divider, Chip } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "hooks/useTranslation";

function SectionTitle({ children }) {
  return (
    <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mt: 3, mb: 1 }}>
      {children}
    </Typography>
  );
}

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

function BulletList({ items }) {
  return (
    <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 1, "& li": { mb: 0.75 } }}>
      {items.map((item) => (
        <li key={item}>
          <Typography fontSize={15} color="#333">
            {item}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

BulletList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function NumberedList({ items }) {
  return (
    <Box component="ol" sx={{ pl: 3, mt: 0.5, mb: 1, "& li": { mb: 1 } }}>
      {items.map((item) => (
        <li key={item}>
          <Typography fontSize={15} color="#333">
            {item}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

NumberedList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function AudioExamplesRow({ count = 3, wrap = false }) {
  const { t } = useTranslation();
  const sources = ["audios/file_example_WAV_1MG.wav", "audios/Brilient.mp3", "audios/Congrats.mp3"];

  return (
    <Box
      display="flex"
      flexDirection="row"
      flexWrap={wrap ? "wrap" : "nowrap"}
      alignItems="center"
      justifyContent="flex-start"
      gap={2}
      sx={{ pl: 1, mt: 1, mb: 1 }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Box key={i} display="flex" alignItems="center" gap={1}>
          <Typography fontSize={14} fontWeight="medium" whiteSpace="nowrap">
            {t("guidelines.examples.example", "Example")} {i + 1}:
          </Typography>
          <audio controls src={sources[i % sources.length]} style={{ maxWidth: 280 }} />
        </Box>
      ))}
    </Box>
  );
}

AudioExamplesRow.propTypes = {
  count: PropTypes.number,
  wrap: PropTypes.bool,
};

export default function GuidelinesContent() {
  const { t } = useTranslation();

  const workflowSteps = [
    t(
      "guidelines.steps.openExam",
      "Open a session from My Exams and choose the sample type tab (Letter, Word, Sentence, Passage, etc.)."
    ),
    t(
      "guidelines.steps.playAudio",
      "Press Play on the waveform. Click anywhere on the waveform to jump to that moment in the recording."
    ),
    t(
      "guidelines.steps.markTimestamps",
      "For short passages (2 minutes or less), mark start/end per line as usual. For long passages (over 2 minutes), the split annotation player divides the recording into segments — see the section below."
    ),
    t(
      "guidelines.steps.transcribe",
      "Copy the canonical text (use the copy icon) and edit the transcription so it matches exactly what the child said."
    ),
    t(
      "guidelines.steps.qualityFields",
      "Fill in Read correctly, BG noise, mistakes/disfluency (if shown), and Fluidity for passages."
    ),
    t(
      "guidelines.steps.save",
      "Save each recording with the disk icon, or use Save / Save & next for the whole tab. Mark the exam complete when all tabs are done."
    ),
  ];

  const timestampTips = [
    t(
      "guidelines.timestamps.tipSeek",
      "Use the waveform like a timeline: click to seek, then press Mark start or Mark end."
    ),
    t(
      "guidelines.timestamps.tipOneLine",
      "Mark one start and one end per canonical line. The line number appears on the waveform."
    ),
    t(
      "guidelines.timestamps.tipClear",
      "Made a mistake? Use the × next to a timestamp to clear it and mark again."
    ),
    t(
      "guidelines.timestamps.tipRequired",
      "Every line needs transcript + start + end before saving, unless you choose a Non-transcribable reason instead."
    ),
  ];

  const splitAnnotationTips = [
    t(
      "guidelines.splitAnnotation.autoSplit",
      "The audio is automatically divided into segments of up to 2 minutes each."
    ),
    t(
      "guidelines.splitAnnotation.playerUi",
      "The player shows a waveform with red split markers and colored segment bands. Each segment has its own reference text and transcription fields."
    ),
    t(
      "guidelines.splitAnnotation.resize",
      "Drag the red split markers or the edges between segment bands to resize segments."
    ),
    t(
      "guidelines.splitAnnotation.addSplit",
      "Click on the waveform to move the playhead, then press Add split. Or double-click on a segment band to split at that position."
    ),
    t("guidelines.splitAnnotation.removeSplit", "Right-click on a red split marker to remove it."),
    t(
      "guidelines.splitAnnotation.constraints",
      "Maximum segment duration is 2 minutes; minimum is 2 seconds."
    ),
    t(
      "guidelines.splitAnnotation.highlighting",
      "Segments that exceed 2 minutes are highlighted in red; segments near the limit show in yellow."
    ),
    t(
      "guidelines.splitAnnotation.perSegment",
      "For each segment: paste the matching portion of the reference text and type what the child actually said."
    ),
  ];

  const transcriptionRules = [
    t(
      "guidelines.rules.copyCanonicalText",
      "Copy the canonical text into the transcription box, then adjust it to match the audio."
    ),
    t(
      "guidelines.rules.listenCarefully",
      "Listen carefully and capture exactly what the child spoke."
    ),
    t(
      "guidelines.rules.writeExactly",
      "Include hesitations (ah, uh, um, etc.) and partial or fragmented words when you hear them."
    ),
    t(
      "guidelines.rules.partialAudio",
      "For passages: if the child skipped a sentence, put a hyphen (-) in that line's transcription. Only do this for partial readings—not for completely blank audio."
    ),
  ];

  const nonTranscribableReasons = [
    {
      key: "blankAudio",
      title: t("guidelines.reasons.blankAudio.title", "Blank audio"),
      description: t(
        "guidelines.reasons.blankAudio.description",
        "No speech at all (silence or noise only). Leave all transcription and timestamp fields empty, then select Blank audio."
      ),
      examples: 3,
    },
    {
      key: "mispronounced",
      title: t("guidelines.reasons.mispronounced.title", "Mispronounced"),
      description: t(
        "guidelines.reasons.mispronounced.description",
        "Speech is present but too distorted or mispronounced to transcribe reliably."
      ),
      examples: 3,
    },
    {
      key: "tooNoisy",
      title: t("guidelines.reasons.tooNoisy.title", "Too noisy"),
      description: t(
        "guidelines.reasons.tooNoisy.description",
        "Background noise or disturbance makes transcription impossible. You may also use BG noise when speech is still transcribable."
      ),
      examples: 3,
    },
    {
      key: "languageMismatch",
      title: t("guidelines.reasons.languageMismatch.title", "Language mismatch"),
      description: t(
        "guidelines.reasons.languageMismatch.description",
        "The child spoke a different language than expected for this item."
      ),
      examples: 3,
    },
    {
      key: "other",
      title: t("guidelines.reasons.other.title", "Other"),
      description: t(
        "guidelines.reasons.other.description",
        "Any other reason the clip cannot be transcribed. Leave transcription and timestamps empty."
      ),
      examples: 3,
    },
  ];

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {t("guidelines.title", "Annotation guidelines")}
      </Typography>
      <Typography fontSize={15} color="text.secondary" sx={{ mb: 2 }}>
        {t(
          "guidelines.intro",
          "Follow these steps for every recording. Accurate timestamps help process only the relevant speech and improve quality checks."
        )}
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          "guidelines.quickTip",
          "Tip: Mark start/end while listening once through the clip—it is faster than transcribing first and guessing times later."
        )}
      </Alert>

      <SectionTitle>{t("guidelines.sections.beforeYouStart", "Before you start")}</SectionTitle>
      <BulletList
        items={[
          t(
            "guidelines.rules.headsetConnection",
            "Use a headset and confirm audio is clear before annotating."
          ),
          t(
            "guidelines.rules.quietEnvironment",
            "Work in a quiet place so you can hear hesitations and word boundaries."
          ),
        ]}
      />

      <SectionTitle>{t("guidelines.sections.workflow", "Step-by-step workflow")}</SectionTitle>
      <NumberedList items={workflowSteps} />

      <SectionTitle>
        {t("guidelines.sections.waveform", "Audio waveform & timestamps")}
      </SectionTitle>
      <Typography fontSize={15} color="#333" sx={{ mb: 1 }}>
        {t(
          "guidelines.timestamps.intro",
          "Each recording shows an interactive waveform instead of a basic player. The timer shows current position / total length."
        )}
      </Typography>
      <BulletList items={timestampTips} />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1, mb: 2 }}>
        <Chip
          label={t("guidelines.timestamps.chipStart", "Mark start")}
          color="primary"
          size="small"
        />
        <Chip
          label={t("guidelines.timestamps.chipEnd", "Mark end")}
          color="primary"
          size="small"
          variant="outlined"
        />
        <Chip
          label={t("guidelines.timestamps.chipRegion", "Colored region = marked segment")}
          size="small"
        />
      </Box>

      <SectionTitle>
        {t("guidelines.sections.splitAnnotation", "Long passage recordings (split annotation)")}
      </SectionTitle>
      <Typography fontSize={15} color="#333" sx={{ mb: 1 }}>
        {t(
          "guidelines.splitAnnotation.intro",
          "Passage recordings longer than 2 minutes use a split annotation player instead of line-by-line timestamps."
        )}
      </Typography>
      <BulletList items={splitAnnotationTips} />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1, mb: 2 }}>
        <Chip
          label={t("guidelines.splitAnnotation.chipAddSplit", "Add split")}
          color="primary"
          size="small"
          variant="outlined"
        />
        <Chip
          label={t("guidelines.splitAnnotation.chipSegment", "Colored band = segment")}
          size="small"
        />
      </Box>

      <SectionTitle>{t("guidelines.sections.transcription", "Transcription rules")}</SectionTitle>
      <BulletList items={transcriptionRules} />

      <SectionTitle>{t("guidelines.sections.quality", "Quality fields")}</SectionTitle>
      <BulletList
        items={[
          t(
            "guidelines.rules.validateReading",
            "Read correctly: Yes if pronunciation matches the canonical text; No if there are errors."
          ),
          t(
            "guidelines.rules.backgroundNoise",
            "BG noise: select a type if you hear disturbance (motor bike, machine, animals, other). Leave blank if the audio is clean."
          ),
          t(
            "guidelines.rules.fluidity",
            "Fluidity (passages only): Yes if the child reads in flowing sentences; No if they read word-by-word."
          ),
          t(
            "guidelines.rules.mistakesDisfluency",
            "Mistakes / disfluency counts: fill in when those columns appear for the sample type."
          ),
        ]}
      />

      <Typography fontSize={15} fontWeight="medium" color="#333" sx={{ mt: 1 }}>
        {t("guidelines.rules.backgroundNoiseShort", "BG noise — listen for examples:")}
      </Typography>
      <AudioExamplesRow count={3} />

      <Divider sx={{ my: 3 }} />

      <SectionTitle>
        {t("guidelines.sections.notTranscribable", "When audio cannot be transcribed")}
      </SectionTitle>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {t(
          "guidelines.notTranscribable.rule",
          "Choose ONE path: either fill every line (transcript + Mark start + Mark end), OR select a Non-transcribable reason and leave all lines empty. Do not mix both."
        )}
      </Alert>

      {nonTranscribableReasons.map(({ key, title, description, examples }) => (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography fontSize={15} fontWeight="bold" color="#333">
            {title}
          </Typography>
          <Typography fontSize={15} color="#555" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
          <AudioExamplesRow count={examples} wrap={key === "mispronounced"} />
        </Box>
      ))}
    </Box>
  );
}
